import { Alert, Box } from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import useGet from "../../../services/hooks/useGet";
import usePost from "../../../services/hooks/usePost";
import { times } from "../../../shared/constants/all-constant";
import { apiEndpoints } from "../../../shared/constants/api-constant";
import { MedicineType } from "../../../shared/constants/enums";
import {
  IUserForms,
  medicationFormFields,
  medicationInitForm,
} from "../../../shared/constants/form.model";
import {
  Heading,
  Input,
  SelectInput,
} from "../../../shared/ReusableComponents";
import DialogBox from "../../../shared/ReusableComponents/DialogBox";
import { NewOffsetInput as OffsetInput } from "../../../shared/ReusableComponents/NewOffsetInput";
import { hideLoader, showLoader } from "../../../store/currentPreferencesSlice";
import CheckboxInput from "../../../shared/ReusableComponents/Checkbox";
import usePut from "../../../services/hooks/usePut";
import { CommonFunctions } from "../../../shared/utils/CommonFunctions";
import { DateTimeInput } from "../../../shared/ReusableComponents/DateTimeInput";
import { DateTimeFormats } from "../../../shared/utils/DateFormatter";
import { AppConfigurationHelper } from "../../../services/AppConfigurationHelper";
import moment from "moment";
import { TimeSlot } from "../../student/Medications/PrescribedMedication/types";

function PrescriptionForm({
  open = false,
  templateId = "",
  editData = null,
  onClick,
}: any) {
  const initialForm = medicationInitForm;

  const [openDailog, setOpenDailog] = useState(open);
  const [error, setError] = useState<string>("");
  const [intervalError, setIntervalError] = useState<string>("");
  const [frequency, setFrequency] = useState<any>([]);
  const [fields, setFields] = useState<IUserForms[]>([...medicationFormFields]);
  const [formData, setFormData] = useState<any>({
    ...initialForm,
    templateId: templateId,
  });
  const [offsetError, setOffsetError] = useState<string>("");
  const { data, getService } = useGet();
  const { postService, loading: postLoading, data: postRes } = usePost();
  const { putService, loading: putLoading, data: putRes } = usePut();
  const lastFetchMedicineType = useRef<string | null>(null);
  const dispatch = useDispatch();
  const offsetErrorText = "Please enter valid input.";
  const TIME_SLOTS: TimeSlot[] = AppConfigurationHelper.Instance().timeSlots;
  const once_only = "once only";
  const isVariableMedicine = formData.medicineType === MedicineType.VARIABLE;
  const isOnceOnlyMedicine =
    formData.medicineType === MedicineType.ONE_TIME_MEDICINE;
  const shouldShowDoseTimeInputs =
    (isVariableMedicine && formData.frequencyId === 9) || isOnceOnlyMedicine;




  const onSelectTime = (time: any) => {
    if (formData.frequencyId !== 9 || formData.frequencyId !== 11) return;
    const splitTime = time.split(":").join("");
    let interval = formData?.interval;
    const index = interval?.indexOf(splitTime);
    if (index > -1) {
      interval.splice(index, 1);
    } else interval = [...interval, splitTime];

    const sortedInterval = interval?.sort((a: string, b: string) =>
      a.localeCompare(b)
    );
    setFormData({
      ...formData,
      interval: sortedInterval,
    });
    setIntervalError("");
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: string
  ) => {
    if (field === "medicineType") {
      changeMedicineType(e.target.value);
    } else if (field === "dosePerDay") {
      const newDose = parseInt(e.target.value);
      const updatedIntervals = (formData.interval || []).slice(0, newDose);
      while (updatedIntervals.length < newDose) updatedIntervals.push("");
      setFormData({
        ...formData,
        dosePerDay: newDose,
        interval: updatedIntervals,
      });
    } else if (field === "frequencyId") {
      frequency.forEach((item: any) => {
        if (item.id === Number(e.target.value)) {
          setFormData({
            ...formData,
            [field]: item.id,
            interval: getInterval(item),
          });
        }
      });
    } else if (
      [
        "isSlowReleased",
        "vteAssessed",
        "prophylaxis",
        "contraindicated",
      ].includes(field)
    ) {
      setFormData({ ...formData, [field]: e.target.checked });
    } else setFormData({ ...formData, [field]: e.target.value });
    setError("");
    setIntervalError("");
  };

  const getInterval = (item: any): string[] => {
    if ((editData?.id && item.id === 9) || editData?.frequencyId === 11) {
      return editData?.interval
        ? editData?.interval?.replaceAll(" ", "").split(",")
        : [];
    }
    if (!Array.isArray(item.interval) && item.interval !== "") {
      return item.interval ? item.interval?.replaceAll(" ", "").split(",") : [];
    }
    return [];
  };

  const changeMedicineType = (value: string) => {
    if (value === MedicineType.PRN) {
      setFields(medicationFormFields);
    } else {
      let fields = medicationFormFields.filter(
        (item: IUserForms) => item.key !== "maxPRN"
      );
      const freqIndex = fields.findIndex(
        (item: IUserForms) => item.key === "frequencyId"
      );
      const doseRemarkIndex = fields.findIndex(
        (item: IUserForms) => item.key === "remarks"
      );
      if (freqIndex > -1 && doseRemarkIndex > -1) {
        const [freqField] = fields.splice(freqIndex, 1);
        fields.splice(doseRemarkIndex, 0, freqField);
      }
      setFields(fields);
    }
    setFormData({
      ...initialForm,
      frequencyId: value === MedicineType.VARIABLE ? 9 : "",
      medicineType: value,
      dosePerDay: value === MedicineType.VARIABLE ? 1 : undefined,
      templateId: templateId,
    });
    setError("");
    setOffsetError("");
    setIntervalError("");
  };

  const handleSubmit = () => {
    setError("");
    setIntervalError("");
    setOffsetError("");
    const payload = { ...formData };
    if (payload.medicineType !== MedicineType.PRN) delete payload.maxPRN;
    if (payload.medicineType === MedicineType.ONE_TIME_MEDICINE)
      payload.dosePerDay = 1;
    if (payload.medicineType === MedicineType.ONE_TIME_MEDICINE) {
      payload.medicineType = "Once Only";
    }
    const values = Object.keys(payload)
      .filter((x) => x !== "offset" && x !== "remarks")
      .some((key) => payload[key] === 0 || payload[key] === "");

    const isVariableFrequency =
      formData.frequencyId === 9 &&
      formData.medicineType === MedicineType.VARIABLE &&
      frequency.find((f: any) => f.id === 9)?.frequencyName === "Variable";
    const requiresDoseTime = isVariableFrequency || isOnceOnlyMedicine;

    if (values) {
      setError("All Fields are Mandatory");
      return;
    }

    if (requiresDoseTime) {
      const intervals = formData.interval || [];
      const expectedCount = isOnceOnlyMedicine ? 1 : formData.dosePerDay || 1;

      const invalidIntervals = intervals.filter(
        (val: string) => !val || val.trim() === ""
      );

      if (intervals.length !== expectedCount || invalidIntervals.length > 0) {
        setError("All Fields are Mandatory");
        // setIntervalError("Please select valid times for all doses.");
        return;
      }
    }

    if (payload.offset === 0) {
      setOffsetError(offsetErrorText);
      return;
    }
    if (payload.frequencyId === 9 || isOnceOnlyMedicine) {
      const intervals = formData?.interval || [];
      payload["interval"] = intervals
        .map((time: string) => time.replace(":", ""))
        .join(", ");
    } else delete payload.interval;

    if (payload.medicineType === MedicineType.VARIABLE) {
      payload["vteAssessed"] = payload["vteAssessed"] ? true : false;
      payload["prophylaxis"] = payload["prophylaxis"] ? true : false;
      payload["contraindicated"] = payload["contraindicated"] ? true : false;
    }
    if (payload.medicineType === MedicineType.REGULAR) {
      payload["isSlowReleased"] = payload["isSlowReleased"] ? true : false;
    }

    dispatch(showLoader());
    const medicineName = CommonFunctions.truncateText(payload.medicineName, 25);
    const message = `${medicineName} ${payload.medicineType
      } Medicine has been Successfully ${editData?.id ? "Updated" : "Added"}.`;

    if (payload.offset === "") {
      payload.offset = 0;
    }
    if (editData.id) {
      delete payload.templateId;
      putService(apiEndpoints.TEMPLATE_MEDICATION, payload, message);
    } else {
      postService(apiEndpoints.TEMPLATE_MEDICATION, payload, message);
    }
  };

  const renderValues = (value: string, field: any) => {
    const selectedOption = field.options?.find(
      (option: any) => option.value === value
    );
    return selectedOption && field.key === "frequencyId"
      ? selectedOption?.label
      : selectedOption?.name;
  };

  useEffect(() => {
    !postLoading && dispatch(hideLoader());
    !putLoading && dispatch(hideLoader());
    if (postRes === 200 || putRes === 200) onClose(true);
  }, [postLoading, postRes, putLoading, putRes]);

  const onClose = (isSubmit = false) => {
    setFormData(initialForm);
    setOpenDailog(false);
    onClick(isSubmit);
  };

  useEffect(() => {
    if (
      !formData?.medicineType ||
      lastFetchMedicineType.current === formData.medicineType
    )
      return;

    lastFetchMedicineType.current = formData?.medicineType;
    dispatch(showLoader());
    getService(
      `${apiEndpoints.FREQUENCY}?isVariable=${formData?.medicineType === MedicineType.VARIABLE
      }`
    ).finally(() => {
      dispatch(hideLoader());
    });
  }, [formData?.medicineType]);

  useEffect(() => {
    if (data) {
      let freqOptions: any = data || [];

      if (formData.medicineType === MedicineType.PRN) {
        // Show all frequencies including PRN for PRN medicine type
        freqOptions = data || [];
        freqOptions = freqOptions.filter((item: any) => item.frequencyName.toLowerCase() !== once_only);

      } else {
        // Exclude PRN frequency for other medicine types
        freqOptions = freqOptions.filter((item: any) => item.id !== 10);

        const isOneTime = formData.medicineType === MedicineType.ONE_TIME_MEDICINE;

        freqOptions = freqOptions.filter((item: any) =>
          isOneTime
            ? item.frequencyName.toLowerCase() === once_only
            : item.frequencyName.toLowerCase() !== once_only
        );

        // If editing and selected frequency is PRN (which was filtered out), add it back
        const selectedFreqExists = freqOptions.some(
          (item: any) => item.id === editData?.frequencyId
        );
        if (editData?.frequencyId && !selectedFreqExists) {
          const selected = data.find(
            (item: any) => item.id === editData.frequencyId
          );
          if (selected) freqOptions.push(selected);
        }
      }

      setFrequency(freqOptions);

      if (
        formData.medicineType === MedicineType.ONE_TIME_MEDICINE &&
        !formData.frequencyId &&
        freqOptions.length === 1
      ) {
        setFormData((prev: any) => ({
          ...prev,
          frequencyId: freqOptions[0].id,
        }));
      }

      fields.forEach((field) => {
        if (field.key === "frequencyId") {
          field.options = freqOptions.map((item: any) => ({
            value: item.id,
            name: [2, 3, 9, 11].includes(item.id)
              ? item.frequencyName
              : `${item.frequencyName} - ${item.interval}`,
            label: item.frequencyName,
          }));
        }
      });
    }
  }, [data, formData.medicineType, editData?.frequencyId]);

  useEffect(() => {
    handleChange(
      {
        target: { value: editData.frequencyId },
      } as React.ChangeEvent<HTMLInputElement>,
      "frequencyId"
    );
  }, [frequency]);

  useEffect(() => {
    setOpenDailog(open);
  }, [open]);

  useEffect(() => {
    if (editData.id) {
      const parsedIntervals = editData?.interval
        ?.split(",")
        .map((i: string) => i.trim())
        .filter((i: string) => i);
      setFormData((prev: any) => ({
        ...prev,
        ...editData,
        offset: editData?.offset || undefined,
        vteAssessed: Boolean(editData?.vteAssessed),
        prophylaxis: Boolean(editData?.prophylaxis),
        contraindicated: Boolean(editData?.contraindicated),
        isSlowReleased: Boolean(editData?.isSlowReleased),
        interval: parsedIntervals,
        dosePerDay: parsedIntervals?.length || 1,
      }));

      if (editData.medicineType === MedicineType.PRN) {
        setFields(medicationFormFields);
      } else {
        let fields = medicationFormFields.filter(
          (item) => item.key !== "maxPRN"
        );

        const freqIndex = fields.findIndex(
          (item) => item.key === "frequencyId"
        );
        const doseRemarkIndex = fields.findIndex(
          (item) => item.key === "remarks"
        );
        if (freqIndex > -1 && doseRemarkIndex > -1) {
          const [freqField] = fields.splice(freqIndex, 1);
          fields.splice(doseRemarkIndex, 0, freqField);
        }
        setFields(fields);
      }
    }
  }, [data, editData]);

  const toMinutes = (time: string): number => {
    const [hh, mm] = time.split(":").map(Number);
    return hh * 60 + mm;
  };

  const isTimeInSlotRange = (
    time: string,
    start: string,
    end: string
  ): boolean => {
    const t = toMinutes(time);
    const s = toMinutes(start);
    const e = toMinutes(end);
    if (s === 0 && e === 0) return time === "00:00"; // Handle 2400 slot
    return t >= s && t <= e;
  };

  const getDisabledSlotRanges = (
    intervals: string[],
    excludeIndex: number = -1
  ) => {
    const ranges: { start: string; end: string }[] = [];

    intervals.forEach((selectedTime, idx) => {
      if (!selectedTime || idx === excludeIndex) return;

      const formattedTime = selectedTime.includes(":")
        ? selectedTime
        : selectedTime.slice(0, 2) + ":" + selectedTime.slice(2);

      const slot = TIME_SLOTS.find(({ start, end }) =>
        isTimeInSlotRange(formattedTime, start, end)
      );

      if (slot) ranges.push({ start: slot.start, end: slot.end });
    });

    return ranges;
  };

  return (
    <React.Fragment>
      <DialogBox
        open={openDailog}
        title="Prescription"
        caption={formData.medicineType ? "(*All Fields are Mandatory)" : ""}
        buttons={{
          isShowBtns: formData.medicineType ? true : false,
          primary: `${editData?.id ? "Update" : "Create"}`,
          secondary: "Cancel",
          isDisabled: formData.medicineType ? false : true,
        }}
        handleSubmit={handleSubmit}
        width="lg"
        fullWidth={true}
        btnClass="dialogActions"
        onClose={(e) => onClose()}
        minHeight={"500px"}
      >
        <Grid container spacing={2}>
          {!formData.medicineType && (
            <Grid size={6}>
              <SelectInput
                name={fields[0].key}
                label={fields[0].label}
                value={formData[fields[0].key]}
                onChange={(e: any) => handleChange(e, fields[0].key)}
                required={fields[0].required}
                size="small"
                options={fields[0].options}
                isValueId={true}
                hasError={
                  fields[0].required && !formData[fields[0].key] && error
                    ? true
                    : false
                }
              />
            </Grid>
          )}
          {formData.medicineType &&
            fields.map((field, index) =>
              field.type === "text" ? (
                <Grid key={index} size={6}>
                  <Input
                    name={field.key}
                    label={field.label}
                    value={formData[field.key]}
                    onChange={(e: any) => handleChange(e, field.key)}
                    required={field.required}
                    size="small"
                    hasError={
                      field.required && !formData[field.key] && error
                        ? true
                        : false
                    }
                    slotProps={{
                      htmlInput: { maxLength: field.max },
                      ...(field.key === "prescribedBy" && {
                        input: {
                          startAdornment: (
                            <span className="prefixName">Dr.</span>
                          ),
                        },
                      }),
                    }}
                  />

                  {formData.medicineType === MedicineType.REGULAR &&
                    field.key === "medicineName" && (
                      <CheckboxInput
                        value={formData["isSlowReleased"]}
                        label="Check If Slow Release"
                        disabled={formData[field.key] ? false : true}
                        onChange={(e: any) => handleChange(e, "isSlowReleased")}
                      />
                    )}
                </Grid>
              ) : field.type === "offset" ? (
                <Grid key={index} size={4}>
                  <OffsetInput
                    onValueChanged={(e) => {
                      setOffsetError("");
                      setFormData({ ...formData, offset: e });
                    }}
                    value={formData[field.key]}
                    isSingleLineLayout={true}
                    errorMessage={offsetError}
                    isRequired={false}
                    onlyHighlightOnError={false}
                  />
                </Grid>
              ) : field.type === "select" ? (
                <>
                  <Grid
                    key={index}
                    size={6}
                    style={{ display: "inline-block" }}
                  >
                    <SelectInput
                      name={field.key}
                      label={field.label}
                      value={
                        field.key === "medicineType" &&
                          typeof formData[field.key] === "string" &&
                          formData[field.key].toLowerCase() === "once only"
                          ? MedicineType.ONE_TIME_MEDICINE
                          : formData[field.key]
                      }
                      onChange={(e: any) => handleChange(e, field.key)}
                      required={field.required}
                      size="small"
                      options={field.options}
                      isValueId={true}
                      hasError={
                        field.required && !formData[field.key] && error
                          ? true
                          : false
                      }
                      disabled={
                        field.key === "medicineType" && !!editData?.medicineType
                      }
                      renderValue={(val) => renderValues(val, field)}
                    />
                  </Grid>

                  {/* Insert dosePerDay dropdown beside frequencyId */}
                  {field.key === "frequencyId" &&
                    formData.medicineType === MedicineType.VARIABLE &&
                    formData.frequencyId === 9 && (
                      <Grid
                        key="dosePerDay"
                        size={2}
                        style={{
                          display: "inline-block",
                          verticalAlign: "top",
                        }}
                      >
                        <SelectInput
                          name="dosePerDay"
                          label="Number of doses in a day"
                          value={formData.dosePerDay || 1}
                          onChange={(e: any) => handleChange(e, "dosePerDay")}
                          required={true}
                          size="small"
                          options={AppConfigurationHelper.Instance().dosePerDayOptions.map(
                            (val: number) => ({
                              name: val,
                              value: val,
                            })
                          )}
                          isValueId={true}
                        />
                      </Grid>
                    )}
                </>
              ) : field.type === "time" ? (
                <Grid
                  key={index}
                  size={12}
                  sx={{ backgroundColor: "var(--color-form)" }}
                >
                  {isVariableMedicine && (
                    <Alert
                      icon={false}
                      style={{
                        backgroundColor: "#e5f6fd",
                      }}
                    >
                      <b>
                        Note: Only one dose can be prescribed per 2-hour time
                        slot.
                      </b>
                    </Alert>
                  )}
                  {(isVariableMedicine || isOnceOnlyMedicine) ? (
                    <Grid container spacing={2} sx={{ width: "100%" }}>
                      <div className="time-intervals-box">
                        {Array.from({
                          length: isOnceOnlyMedicine
                            ? 1
                            : formData.dosePerDay || 1,
                        }).map(
                          (_, idx) => {
                            const disabledRanges = getDisabledSlotRanges(
                              formData.interval || [],
                              idx
                            );

                            const shouldDisableTime = (
                              value: any,
                              view: "hours" | "minutes"
                            ) => {
                              if (isOnceOnlyMedicine) return false;

                              const time = moment(value).format("HH:mm");

                              for (const { start, end } of disabledRanges) {
                                if (isTimeInSlotRange(time, start, end)) {
                                  return true;
                                }
                              }

                              return false;
                            };

                            return (
                              <Grid
                                key={`dose-time-${idx}`}
                                style={{ textAlign: "center" }}
                              >
                               { !isOnceOnlyMedicine && <p style={{ marginBottom: 8 }}>
                                  Dose {idx + 1}
                                </p>}
                                <DateTimeInput
                                  type="time"
                                  label={`${isOnceOnlyMedicine ? "Time" : `Time ${idx + 1}`}`}
                                  name={`interval-${idx}`}
                                  format={DateTimeFormats.TimeFormat}
                                  timeSteps={{ minutes: 1 }}
                                  value={formData.interval?.[idx] || ""}
                                  size="small"
                                  shouldDisableTime={shouldDisableTime}
                                  onChange={(val) => {
                                    const updated = [
                                      ...(formData.interval || []),
                                    ];
                                    updated[idx] = moment(val).format("HH:mm");
                                    setFormData({
                                      ...formData,
                                      interval: updated,
                                    });
                                  }}
                                  hasError={
                                    error &&
                                    (!formData.interval?.[idx] ||
                                      formData.interval[idx]?.trim() === "")
                                  }
                                />
                              </Grid>
                            );
                          }
                        )}
                      </div>
                    </Grid>
                  ) : (
                    <div
                      className={`time-box ${formData?.interval?.length === 0 &&
                        (error || intervalError)
                        ? "error"
                        : ""
                        }`}
                    >
                      {times &&
                        times.map((time, idx) => (
                          <span
                            key={`${idx}`}
                            className={`${formData.frequencyId === 9
                              ? "time-variable"
                              : "time-interval"
                              } ${formData?.interval?.includes(
                                time.key.split(":").join("")
                              )
                                ? "select"
                                : ""
                              }`}
                            onClick={(e) => onSelectTime(time.key)}
                          >
                            {time.key.split(":").join("")}
                          </span>
                        ))}
                    </div>
                  )}
                  {intervalError && (
                    <span style={{ color: "var(--color-error-main)" }}>
                      {intervalError}
                    </span>
                  )}
                </Grid>
              ) : null
            )}
        </Grid>
        <Grid container spacing={2}>
          {formData.medicineType && (
            <Grid
              size={6}
              key={`${formData.medicineType}_section`}
              className="variableSection"
            >
              <Heading text="VTE risk assessed:" type="h6" isBold={true} />
              <CheckboxInput
                label="Yes"
                value={formData["vteAssessed"]}
                onChange={(e: any) => handleChange(e, "vteAssessed")}
              />
              <CheckboxInput
                label="Prophylaxis not required"
                value={formData["prophylaxis"]}
                onChange={(e: any) => handleChange(e, "prophylaxis")}
              />
              <CheckboxInput
                label="Contraindicated"
                value={formData["contraindicated"]}
                onChange={(e: any) => handleChange(e, "contraindicated")}
              />
            </Grid>
          )}
        </Grid>
        {error && (
          <Box
            sx={{
              borderBottom: "2px solid var(--color-border-model)",
              marginTop: "19px",
            }}
          >
            <span style={{ color: "var(--color-error-main)" }}>{error}</span>
          </Box>
        )}
      </DialogBox>
    </React.Fragment>
  );
}

export default PrescriptionForm;
